import {
  Feature,
  Position,
  Properties,
  Polygon as TurfPolygon,
  area,
  centroid,
  intersect,
  polygon,
  union,
} from '@turf/turf';

import { deepCopy } from './deepCopy';
import { Event } from '../interfaces';
import { getEventImageSize } from './getEventVideos';

enum CameraGpsHomographyCheckResultType {
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
}

type Polygon = Feature<TurfPolygon, Properties>;

export class CameraGpsHomographyCheck {
  private static readonly IOU_THRESHOLD = 0.45;
  private static readonly IOM_THRESHOLD = 0.7;
  private static readonly INCLINATION_ANGLE_THRESHOLD = 30;

  private cameraGpsHomographyMatrix: number[][];

  private goldStandardPolygonPoints: Position[] = [
    [-13.1131048, 6.0718197],
    [13.1131048, 6.0718197],
    [2.58941079, -9.56122065],
    [-2.58941079, -9.56122065],
    [-13.1131048, 6.0718197],
  ];

  private goldStandardPolygon = polygon([this.goldStandardPolygonPoints]);

  projectPolygon(event: Event): Polygon {
    // Project corner points from the image space to the GPS space using the homography matrix

    const dimensions = getEventImageSize(event);

    // Define corner points in image space
    const margin = 0;
    const x_min: number = 0 + margin;
    const x_max: number = dimensions.width - margin;
    const y_min: number = 400 + margin;
    const y_max: number = dimensions.height - margin;

    // Store the corner points in clockwise order starting from the top left corner
    const x_corners: number[] = [x_min, x_max, x_max, x_min];
    const y_corners: number[] = [y_min, y_min, y_max, y_max];

    // Project the corner points to the GPS space
    let corner_points: number[][] = vstack([x_corners, y_corners]);

    corner_points = perspectiveTransform(corner_points, this.cameraGpsHomographyMatrix);

    // Shift enclosing polygon centroid to origin
    const polygon_points = deepCopy(corner_points);
    polygon_points.push(corner_points[0]); // Close the polygon
    const poly = polygon([polygon_points]);
    const polygon_centroid = centroid(poly);
    const x_center = polygon_centroid.geometry.coordinates[0];
    const y_center = polygon_centroid.geometry.coordinates[1];

    // Subtract [x_center, y_center] from each point in polygon_points
    const adjusted_points: Point[] = polygon_points.map((point) => {
      const [x, y] = point;
      return [x - x_center, y - y_center];
    });

    const projected_polygon = polygon([adjusted_points]);

    return projected_polygon;
  }

  calculateIom(projectedPolygon: Polygon): number {
    // Calculating intersection over minimum area.
    const minArea = Math.min(area(this.goldStandardPolygon), area(projectedPolygon));

    // Calculate the intersection of the two polygons
    const intersection = intersect(this.goldStandardPolygon, projectedPolygon);
    // If there is an intersection, calculate its area; otherwise, return 0
    const intersectionArea = intersection ? area(intersection) : 0;

    return intersectionArea / (minArea + 1e-6);
  }

  calculateIou(projectedPolygon: Polygon): number {
    // Calculating intersection over union.

    // Calculate the intersection of the two polygons
    const intersection = intersect(this.goldStandardPolygon, projectedPolygon);

    // If there is an intersection, calculate its area; otherwise, return 0
    if (!intersection) {
      return 0;
    }

    const uni = union(this.goldStandardPolygon, projectedPolygon);
    if (uni === null) {
      return 0;
    }
    const union_area = area(uni);

    return area(intersection) / (union_area + 1e-6);
  }

  // eslint-disable-next-line class-methods-use-this
  calculateInclinationAngle(point1: number[], point2: number[]): number {
    const angle = Math.atan2(point2[1] - point1[1], point2[0] - point1[0] + 1e-6);
    return Math.abs(angle * (180 / Math.PI));
  }

  calculateSteepestInclinationAngle(projectedPolygon: Polygon): number {
    // Extract polygon points, assuming projectedPolygon is an array of Points
    const polygonPoints = projectedPolygon.geometry.coordinates[0].slice(0, -1); // Exclude last point (similar to Python's [:-1])

    // Calculate the inclination angle of the top edge and the bottom edge of the projected polygon
    const topEdgeInclinationAngle = this.calculateInclinationAngle(polygonPoints[0], polygonPoints[1]);
    const bottomEdgeInclinationAngle = this.calculateInclinationAngle(polygonPoints[3], polygonPoints[2]);

    // Convert the inclination angles to degrees and take the absolute value
    // Note: Assuming calculate_inclination_angle returns angles in radians and needs conversion if required
    const topEdgeInclinationAngleDegrees = Math.abs(topEdgeInclinationAngle);
    const bottomEdgeInclinationAngleDegrees = Math.abs(bottomEdgeInclinationAngle);

    // Calculate the steepest inclination angle
    const steepestInclinationAngle = Math.max(topEdgeInclinationAngleDegrees, bottomEdgeInclinationAngleDegrees);

    return steepestInclinationAngle;
  }

  public runCameraGpsHomographyCheck(
    cameraGpsHomographyMatrix: number[][],
    event: Event,
  ): {
    result: CameraGpsHomographyCheckResultType;
    message: string;
  } {
    this.cameraGpsHomographyMatrix = cameraGpsHomographyMatrix;

    try {
      const projectedPolygon = this.projectPolygon(event);
      const iou = this.calculateIou(projectedPolygon);
      const iom = this.calculateIom(projectedPolygon);
      const steepestInclinationAngle = this.calculateSteepestInclinationAngle(projectedPolygon);

      if (
        iou >= CameraGpsHomographyCheck.IOU_THRESHOLD &&
        iom >= CameraGpsHomographyCheck.IOM_THRESHOLD &&
        steepestInclinationAngle <= CameraGpsHomographyCheck.INCLINATION_ANGLE_THRESHOLD
      ) {
        return {
          result: CameraGpsHomographyCheckResultType.SUCCESS,
          message: 'Camera GPS Homography check passed.',
        };
      }

      let message = `Camera GPS Homography check failed: `;
      if (iou < CameraGpsHomographyCheck.IOU_THRESHOLD) {
        message += `IOU: ${iou.toFixed(3)} < ${CameraGpsHomographyCheck.IOU_THRESHOLD}. `;
      }
      if (iom < CameraGpsHomographyCheck.IOM_THRESHOLD) {
        message += `IOM: ${iom.toFixed(3)} < ${CameraGpsHomographyCheck.IOM_THRESHOLD}. `;
      }
      if (steepestInclinationAngle > CameraGpsHomographyCheck.INCLINATION_ANGLE_THRESHOLD) {
        message += `Inclination angle: ${steepestInclinationAngle.toFixed(3)} > 
                            ${CameraGpsHomographyCheck.INCLINATION_ANGLE_THRESHOLD}. `;
      }

      return {
        result: CameraGpsHomographyCheckResultType.FAIL,
        message,
      };
    } catch (error) {
      return {
        result: CameraGpsHomographyCheckResultType.FAIL,
        message: `Camera GPS Homography check failed: ${error?.message}`,
      };
    }
  }
}

// Equivalent of np.vstack
function vstack(arrays: number[][]): number[][] {
  const result: number[][] = [];
  for (let i = 0; i < arrays[0].length; i++) {
    const row: number[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const array of arrays) {
      row.push(array[i]);
    }
    result.push(row);
  }
  return result;
}

// Assuming corner_points is an array of [x, y] pairs and camera_gps_homography_matrix is a 3x3 matrix
type Point = number[];
type Matrix = number[][];

function perspectiveTransform(points: Point[], matrix: Matrix): Point[] {
  /*
    // Example usage
    let corner_points: Point[] = [[x1, y1], [x2, y2], [x3, y3], [x4, y4]]; // Fill in with actual points
    let camera_gps_homography_matrix: Matrix = [
        [h11, h12, h13],
        [h21, h22, h23],
        [h31, h32, h33]
    ]; // Fill in with actual matrix values
    let transformedPoints = perspectiveTransform(corner_points, camera_gps_homography_matrix);

    console.log(transformedPoints);
    */

  return points.map((point) => {
    const [x, y] = point;
    const w = matrix[2][0] * x + matrix[2][1] * y + matrix[2][2];
    const newX = (matrix[0][0] * x + matrix[0][1] * y + matrix[0][2]) / w;
    const newY = (matrix[1][0] * x + matrix[1][1] * y + matrix[1][2]) / w;
    return [newX, newY];
  });
}
